/**
 * AppShell — the top-level mount that hosts the app controller and swaps
 * in the correct screen for each state.
 *
 * Lifecycle:
 * - Instantiates a single {@link AppController} wired to the real api
 *   client, clock, and `setTimeout`/`clearTimeout` pair.
 * - Mounts a `<div class="roast-shell">` into the shadow root and
 *   delegates screen rendering to four mount helpers (entry, loading,
 *   result, error).
 * - Subscribes to controller state changes. Screens are only (un)mounted
 *   when the *mount group* changes; intra-screen updates (e.g. the
 *   reveal progressing from `revealing` to `result`) are handled by the
 *   screen itself, because the result screen owns reveal timers across
 *   that transition.
 *
 * See `.kiro/specs/marketing-roast-machine/design.md` — "Component Tree"
 * and "Runtime Flow".
 */

import {
  createAppController,
  type AppController,
} from '../state/appController';
import type { AppState } from '../state/stateMachine';
import { requestRoast } from '../services/apiClient';
import { mountEntryScreen } from './EntryScreen';
import { mountLoadingScreen } from './LoadingScreen';
import { mountResultScreen } from './ResultScreen';
import { mountErrorScreen } from './ErrorScreen';
import { createViewportMatcher } from './NarrowViewportNotice';

/** Configuration required by {@link mountAppShell}. */
export type AppShellConfig = {
  /** Base URL for the roast API, e.g. "/api". Forwarded to the api client. */
  apiBase: string;
};

/** Teardown function returned by {@link mountAppShell}. */
export type AppShellTeardown = () => void;

/**
 * A "mount group" is the coarse key that drives screen (un)mounting.
 * Both `revealing` and `result` share the `'result'` group because the
 * ResultScreen owns reveal timers across that boundary and should not
 * be torn down mid-reveal.
 */
type MountGroup = 'idle' | 'loading' | 'result' | 'error';

/**
 * Project an {@link AppState} onto its {@link MountGroup}.
 */
function toMountGroup(state: AppState): MountGroup {
  switch (state.kind) {
    case 'idle':
      return 'idle';
    case 'loading':
      return 'loading';
    case 'revealing':
    case 'result':
      return 'result';
    case 'error':
      return 'error';
  }
}

/**
 * Mount the app shell into the provided shadow root. Returns a teardown
 * function that unsubscribes from the controller, tears down the active
 * screen, and removes the root container.
 */
export function mountAppShell(
  shadowRoot: ShadowRoot,
  config: AppShellConfig,
): AppShellTeardown {
  const controller: AppController = createAppController({
    api: {
      requestRoast: (input, opts) =>
        requestRoast(input, { ...opts, apiBase: config.apiBase }),
    },
    now: () => Date.now(),
    schedule: (fn, ms) => {
      const handle = setTimeout(fn, ms);
      return {
        cancel: () => clearTimeout(handle),
      };
    },
  });

  const root = document.createElement('div');
  root.className = 'roast-shell app-shell';
  shadowRoot.appendChild(root);

  const viewport = createViewportMatcher();

  let currentGroup: MountGroup | null = null;
  let currentTeardown: (() => void) | null = null;
  let torn = false;

  /**
   * Mount the screen for `group`, using `state` as the initial state for
   * screens that need one (result, error). Tears down the previous
   * screen first so DOM and timers don't leak across transitions.
   */
  const mountGroup = (group: MountGroup, state: AppState): void => {
    if (currentTeardown !== null) {
      currentTeardown();
      currentTeardown = null;
    }

    switch (group) {
      case 'idle':
        currentTeardown = mountEntryScreen(root, controller, viewport);
        break;
      case 'loading':
        currentTeardown = mountLoadingScreen(root);
        break;
      case 'result': {
        // Safe: toMountGroup only maps revealing/result here.
        if (state.kind !== 'revealing' && state.kind !== 'result') {
          return;
        }
        currentTeardown = mountResultScreen(root, controller, state);
        break;
      }
      case 'error': {
        if (state.kind !== 'error') {
          return;
        }
        currentTeardown = mountErrorScreen(root, controller, state);
        break;
      }
    }
    currentGroup = group;
  };

  const handleStateChange = (next: AppState): void => {
    if (torn) {
      return;
    }
    const nextGroup = toMountGroup(next);
    if (nextGroup !== currentGroup) {
      mountGroup(nextGroup, next);
    }
    // Intra-group updates (e.g. REVEAL_TICK inside `revealing`) are
    // delivered to the active screen via its own `controller.subscribe`
    // subscription, so there's nothing more to do here.
  };

  // Prime the initial screen.
  const initial = controller.getState();
  mountGroup(toMountGroup(initial), initial);

  const unsubscribe = controller.subscribe(handleStateChange);

  return () => {
    if (torn) {
      return;
    }
    torn = true;
    unsubscribe();
    if (currentTeardown !== null) {
      currentTeardown();
      currentTeardown = null;
    }
    root.remove();
  };
}
